/**
 * Wire types for the BFF trail response + a runtime type-guard
 * that validates incoming payloads at the trust boundary.
 *
 * Stream A PR 2 / commit M5 — companion to
 * :file:`snapshot-types.ts`. The BFF's ``GET /api/query/trail/
 * {producer_id}`` returns the same :class:`StatusItem` shape as
 * snapshot does, just envelope-wrapped in :class:`TrailResponse`
 * (items + ``next_cursor`` keyset pagination per REQ-QUERY-018 +
 * ``duration_ms``). See ``packages/meridian_bff/api/openapi.yaml``
 * §components/schemas/TrailResponse.
 *
 * # Why hand-rolled guards rather than zod
 *
 * Same posture as :file:`snapshot-types.ts`: zod isn't yet a UI
 * dependency. CLAUDE.md §2.2 mandates ``zod`` at trust boundaries;
 * until the tech-stack addition lands the hand-rolled guard is the
 * stand-in. Re-uses :func:`isSnapshotItem` so the per-item shape
 * stays a single source of truth between snapshot + trail paths.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering).
 *   - REQ-UI-035 (BFF-only).
 *   - CLAUDE.md §2.2 (zod at trust boundaries — deferred).
 *   - CLAUDE.md §5.10 (typed openapi.yaml is single source of
 *     truth — alias rather than redefine).
 *   - packages/meridian_bff/api/openapi.yaml §TrailResponse.
 *   - packages/meridian_bff/src/meridian_bff/_query_routes.py
 *     §trail_route.
 */
import type { components } from "@/lib/api-types.gen";
import { isSnapshotItem, type SnapshotItem } from "@/map/snapshot-types";

/**
 * One status record in a trail — same shape as a snapshot item
 * (BFF returns :class:`StatusItem` from both endpoints). Re-aliased
 * locally so callers can pass :type:`TrailItem` semantically even
 * though it's structurally a :type:`SnapshotItem`.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export type TrailItem = SnapshotItem;

/**
 * The BFF's trail-response envelope — generated from the BFF's
 * Pydantic :class:`TrailResponse`.
 *
 * Carries ``items`` (time-ascending per the BFF route docstring),
 * ``next_cursor`` (opaque keyset continuation; ``null`` when no
 * more pages), and ``duration_ms``.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - REQ-QUERY-018 (keyset pagination).
 *   - packages/meridian_bff/api/openapi.yaml §TrailResponse.
 */
export type TrailResponse = components["schemas"]["TrailResponse"];

/**
 * Type guard: validate an arbitrary value matches the
 * :type:`TrailResponse` shape.
 *
 * Returns ``false`` on first deviation. Per-item shape is delegated
 * to :func:`isSnapshotItem` (the wire schema is shared). Callers
 * should treat ``false`` as a 5xx-shaped failure and surface a
 * fail-safe state (operator-visibility per CLAUDE.md §8.1).
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - CLAUDE.md §8.1 (fail-safe; operator-visibility).
 */
export function isTrailResponse(value: unknown): value is TrailResponse {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.items)) {
    return false;
  }
  if (typeof obj.duration_ms !== "number") {
    return false;
  }
  // ``next_cursor`` is either an opaque string or null per the BFF
  // contract (see ``packages/meridian_bff/api/openapi.yaml``
  // §TrailResponse.next_cursor anyOf [string, null]).
  if (obj.next_cursor !== null && typeof obj.next_cursor !== "string") {
    return false;
  }
  return obj.items.every(isSnapshotItem);
}
